<template>
  <div class="card">
    <div class="bg-blue-light p-1">
      <TitleButton
          :showBtn="true"
          :title="pageTitle"
          @onClickCloseButton="goBack"
      />
    </div>

    <div v-if="errorMessage" class="px-2 mt-2">
      <div class="alert alert-danger mb-0">
        <div class="alert-body p-1">
          <div class="fw-bold mb-0">{{ errorMessage }}</div>
        </div>
      </div>
    </div>

    <div v-if="hasPreview && !canPost" class="px-2 mt-2">
      <div class="alert alert-danger mb-0">
        <div class="alert-body p-1">
          <div class="fw-bold mb-0">Account Head is required for: {{ unlinkedPayHeadNames }}</div>
        </div>
      </div>
    </div>

    <div v-if="hasPreview" class="px-2 mt-2">
      <div class="row gy-1">
        <div class="col-12 col-sm-6 col-lg-3">
          <div class="summary-tile h-100">
            <div class="summary-label">Payroll Period</div>
            <div class="summary-value">{{ periodLabel }}</div>
          </div>
        </div>
        <div class="col-12 col-sm-6 col-lg-3">
          <div class="summary-tile h-100">
            <div class="summary-label">Total Debit</div>
            <div class="summary-value summary-amount">{{ formatAmount(preview.debit_total) }}</div>
          </div>
        </div>
        <div class="col-12 col-sm-6 col-lg-3">
          <div class="summary-tile h-100">
            <div class="summary-label">Total Credit</div>
            <div class="summary-value summary-amount">{{ formatAmount(preview.credit_total) }}</div>
          </div>
        </div>
        <div class="col-12 col-sm-6 col-lg-3">
          <div class="summary-tile summary-tile-accent h-100">
            <div class="summary-label">Total Net Payable</div>
            <div class="summary-value summary-amount">{{ formatAmount(preview.total_net_payable) }}</div>
          </div>
        </div>
        <div v-if="!isBalanced" class="col-12 col-lg-4">
          <div class="summary-tile summary-tile-danger h-100">
            <div class="summary-label">Balance Check</div>
            <div class="summary-value"><span class="badge bg-danger">Out of balance</span></div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isPosted" class="px-2 mt-2">
      <div class="posted-bar d-flex flex-wrap align-items-center gap-2">
        <span class="badge bg-success">Journal Posted</span>
        <span class="posted-item"><span class="posted-label">Voucher No</span> {{ preview.voucher_no }}</span>
        <span class="posted-item"><span class="posted-label">Date</span> {{ preview.format_voucher_date }}</span>
        <span class="posted-item"><span class="posted-label">Status</span> {{ preview.voucher_status }}</span>
        <span v-if="preview.note" class="posted-item"><span class="posted-label">Note</span> {{ preview.note }}</span>
      </div>
    </div>

    <div v-if="canCreate" class="px-2 mt-2">
      <div class="row gy-1">
        <div class="col-12 col-md-6 col-lg-4">
          <label class="form-label fw-bold" for="payableAccount">Payable Account <span class="text-danger">*</span></label>
          <AsyncSelect
              placeholder="Select payable account"
              v-model="selectedPayableAccount"
              :api-service="fetchAccountHeadsList"
              :reduce="head => head"
              label="name"
              :additional-query="accountHeadQuery"
              :format-label="formatPatientLabel"
          />
        </div>
        <div class="col-12 col-md-6 col-lg-3">
          <label class="form-label fw-bold" for="voucherDate">Voucher Date <span class="text-danger">*</span></label>
          <input id="voucherDate" class="form-control" type="date" v-model="formData.date">
        </div>
        <div class="col-12 col-md-6 col-lg-3">
          <label class="form-label fw-bold" for="voucherNote">Note</label>
          <input id="voucherNote" class="form-control" type="text" v-model="formData.note" placeholder="Optional narration">
        </div>
        <div class="col-12 col-lg-2 d-flex align-items-end">
          <button
              :disabled="!canSubmit"
              class="btn btn-primary w-100"
              @click="submitJournal"
          >
            Create JV
          </button>
        </div>
      </div>
    </div>

    <div v-if="hasPreview" class="px-2 mt-2 pb-2">
      <div class="table-responsive">
        <table class="table table-bordered table-hover mb-0">
          <thead>
          <tr>
            <th>Pay Head</th>
            <th>Type</th>
            <th>Account Head</th>
            <th class="text-end nowrap">Debit</th>
            <th class="text-end nowrap">Credit</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="payHead in allPayHeads" :key="payHead.pay_head_id">
            <td>{{ payHead.name }}</td>
            <td class="text-capitalize">{{ payHead.type }}</td>
            <td>
              <span v-if="payHead.account_head_id">{{ payHead.account_head_name }}</span>
              <AsyncSelect
                  v-else
                  :key="payHead.pay_head_id"
                  input-class="pay-head-account-select"
                  placeholder="Select Account Head"
                  :model-value="null"
                  :api-service="fetchAccountHeadsList"
                  :reduce="name => name.id"
                  :additional-query="accountHeadQuery"
                  :format-label="formatPatientLabel"
                  :option-selected="value => linkAccountHead(payHead, value)"
                  append-to-body
                  :calculate-position="withPopper"
              />
            </td>
            <td class="text-end nowrap">{{ payHead.type === 'earning' ? formatAmount(payHead.amount) : '' }}</td>
            <td class="text-end nowrap">{{ payHead.type === 'deduction' ? formatAmount(payHead.amount) : '' }}</td>
          </tr>
          <tr v-if="!isPosted">
            <td colspan="2" class="fw-bold">Payable Account</td>
            <td>{{ payableAccountName }}</td>
            <td class="text-end nowrap">{{ hasValue(payableDebit) ? formatAmount(payableDebit) : '' }}</td>
            <td class="text-end nowrap">{{ hasValue(payableCredit) ? formatAmount(payableCredit) : '' }}</td>
          </tr>
          <tr v-for="line in payableLines" :key="line.account_head_name">
            <td colspan="2" class="fw-bold">Payable Account</td>
            <td>{{ line.account_head_name }}</td>
            <td class="text-end nowrap">{{ hasValue(line.debit) ? formatAmount(line.debit) : '' }}</td>
            <td class="text-end nowrap">{{ hasValue(line.credit) ? formatAmount(line.credit) : '' }}</td>
          </tr>
          </tbody>
          <tfoot>
          <tr class="total-row">
            <th colspan="3" class="text-end">Total</th>
            <th class="text-end nowrap">{{ formatAmount(payHeadDebitTotal + payableDebit) }}</th>
            <th class="text-end nowrap">{{ formatAmount(payHeadCreditTotal + payableCredit) }}</th>
          </tr>
          </tfoot>
        </table>
        <p v-if="!allPayHeads.length" class="text-center mt-5">No payroll process found under this statement</p>
      </div>
    </div>

    <Loader v-if="loading"/>
  </div>
</template>

<script setup>
import {computed, inject, onMounted, ref, watch} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import TitleButton from '@/components/atom/TitleButton'
import Loader from '@/components/atom/LoaderComponent'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import handleHrPayroll from '@/services/modules/hrPayroll/hrPayroll'
import handleJournal from '@/services/modules/journal'
import {useAsyncDropdownHelper} from '@/services/utils/asyncDropdownHelper'
import {createPopper} from '@popperjs/core'

const $route = useRoute()
const $router = useRouter()
const showError = inject('showError')
const showSuccess = inject('showSuccess')

const {fetchPayrollJournalPreview, storePayrollJournal, linkPayHeadAccountHead} = handleHrPayroll()
const {fetchAccountHeadsList} = handleJournal()
const {formatPatientLabel} = useAsyncDropdownHelper()

const loading = ref(false)
const submitting = ref(false)
const preview = ref(null)
const selectedPayableAccount = ref(null)
const errorMessage = ref('')
const formData = ref({
  date: '',
  payable_account_head_id: null,
  note: ''
})

const accountHeadQuery = computed(() => ({
  cash_bank_hide: 'yes',
  status: 'active'
}))

watch(selectedPayableAccount, (newValue) => {
  formData.value.payable_account_head_id = newValue ? newValue.id : null
})

const hasPreview = computed(() => preview.value !== null)

const allPayHeads = computed(() => {
  if (!preview.value) return []
  if (!preview.value.masters) return []
  return preview.value.masters.flatMap(master => master.pay_heads)
})

const payHeadDebitTotal = computed(() => {
  return allPayHeads.value
      .filter(payHead => payHead.type === 'earning')
      .reduce((sum, payHead) => sum + Number(payHead.amount), 0)
})

const payHeadCreditTotal = computed(() => {
  return allPayHeads.value
      .filter(payHead => payHead.type === 'deduction')
      .reduce((sum, payHead) => sum + Number(payHead.amount), 0)
})

const netPayable = computed(() => {
  return payHeadDebitTotal.value - payHeadCreditTotal.value
})

const payableDebit = computed(() => {
  if (netPayable.value < 0) return Math.abs(netPayable.value)
  return 0
})

const payableCredit = computed(() => {
  if (netPayable.value < 0) return 0
  return netPayable.value
})

const payableAccountName = computed(() => {
  if (!selectedPayableAccount.value) return 'Select payable account above'
  return selectedPayableAccount.value.name
})

const unlinkedPayHeadNames = computed(() => {
  if (!preview.value) return ''
  if (!preview.value.unlinked_pay_heads) return ''
  return preview.value.unlinked_pay_heads.join(', ')
})

const isPosted = computed(() => {
  if (!preview.value) return false
  return preview.value.is_posted === true
})

const payableLines = computed(() => {
  if (!isPosted.value) return []
  if (!preview.value.lines) return []
  return preview.value.lines.filter(line => line.line_label === 'Net Payable')
})

const hasValue = (amount) => {
  if (amount === null || amount === undefined) return false
  return Number(amount) !== 0
}

const canCreate = computed(() => {
  if (!hasPreview.value) return false
  return !isPosted.value
})

const pageTitle = computed(() => {
  if (isPosted.value) return 'Payroll Journal Voucher'
  return 'Create Payroll Journal'
})

const periodLabel = computed(() => {
  if (!preview.value) return ''
  return `${preview.value.format_start_date} to ${preview.value.format_end_date}`
})

const isBalanced = computed(() => {
  if (!preview.value) return false
  return Number(preview.value.debit_total) === Number(preview.value.credit_total)
})

const isFormFilled = computed(() => {
  if (!formData.value.date) return false
  if (!formData.value.payable_account_head_id) return false
  return true
})

const canPost = computed(() => {
  if (!preview.value) return false
  return preview.value.can_post === true
})

const canSubmit = computed(() => {
  if (submitting.value) return false
  if (!isFormFilled.value) return false
  if (!canPost.value) return false
  return isBalanced.value
})

const formatAmount = (amount) => {
  if (amount === null || amount === undefined) return '0.00'
  return Number(amount).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})
}

const withPopper = (dropdownList, component, {width}) => {
  dropdownList.style.width = width
  const popper = createPopper(component.$refs.toggle, dropdownList, {
    placement: 'bottom',
    modifiers: [
      {name: 'offset', options: {offset: [0, 2]}}
    ]
  })
  return () => popper.destroy()
}

const getQuery = () => {
  return `?company_id=${$route.params.companyId}`
}

const getPreview = () => {
  return fetchPayrollJournalPreview($route.params.payrollStatementId, getQuery())
      .then(res => {
        if (!res.status) {
          errorMessage.value = res.message
          return
        }
        preview.value = res.data
        formData.value.date = res.data.end_date
      })
}

const loadPage = () => {
  loading.value = true
  getPreview().finally(() => loading.value = false)
}

const linkAccountHead = (payHead, accountHeadId) => {
  if (!accountHeadId) return
  if (!confirm(`Are you sure you want to update the Account Head for ${payHead.name}?`)) return
  const data = {
    company_id: $route.params.companyId,
    pay_head_id: payHead.pay_head_id,
    account_head_id: accountHeadId
  }
  loading.value = true
  linkPayHeadAccountHead(data)
      .then(res => {
        if (!res.status) {
          showError(res.message)
          return
        }
        showSuccess(res.message)
        getPreview()
      })
      .finally(() => {
        loading.value = false
      })
}

const submitJournal = () => {
  if (!isFormFilled.value) {
    showError('Please select payable account and voucher date')
    return
  }
  if (!canPost.value) {
    showError('Please link an Account Head for every pay head before creating the journal')
    return
  }
  if (!isBalanced.value) {
    showError('Debit and credit totals do not match')
    return
  }
  submitting.value = true
  loading.value = true
  storePayrollJournal($route.params.payrollStatementId, formData.value, getQuery())
      .then(res => {
        if (!res.status) {
          showError(res.message)
          return
        }
        showSuccess(res.message)
        getPreview()
      })
      .finally(() => {
        submitting.value = false
        loading.value = false
      })
}

const goBack = () => {
  $router.push({
    name: 'payroll-journal',
    params: {
      companyId: $route.params.companyId,
      moduleId: $route.params.moduleId,
      menuId: $route.params.menuId,
      pageId: $route.params.pageId
    }
  })
}

onMounted(() => {
  loadPage()
})
</script>

<style scoped>
.summary-tile {
  border: 1px solid #e4e6eb;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  background: #fbfcfd;
}

.summary-tile-accent {
  background: #dbe7f2;
  border-color: #c5d7e8;
}

.summary-tile-danger {
  background: #fceaea;
  border-color: #f3c2c2;
}

.summary-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6e6b7b;
  margin-bottom: 0.25rem;
}

.summary-value {
  font-size: 1rem;
  font-weight: 600;
  color: #4b4b4b;
}

.summary-amount {
  font-size: 1.35rem;
}

.posted-bar {
  border: 1px solid #e4e6eb;
  border-left: 3px solid #28c76f;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  background: #fbfcfd;
}

.posted-item {
  font-size: 0.875rem;
  color: #4b4b4b;
}

.posted-label {
  color: #6e6b7b;
  margin-right: 0.25rem;
}

.nowrap {
  white-space: nowrap;
}

.total-row th {
  background-color: #f2f2f2;
}

table thead th {
  white-space: nowrap;
}
</style>

<style>
.pay-head-account-select .vs__dropdown-menu {
  z-index: 1055;
}
</style>
